import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useNotifications } from "@/context/NotificationsContext";
import { initPushNotifications } from "@/lib/push";
import {
    HouseSimple,
    CalendarBlank,
    Newspaper,
    User,
    GearSix,
    SignOut,
    Megaphone,
    ChatCircleDots,
    Trophy as Whistle,
} from "@phosphor-icons/react";

const roleLabels = {
    admin: "Amministratore",
    coach: "Allenatore",
    player: "Giocatore",
    parent: "Genitore",
};

export default function Layout() {
    const { user, logout } = useAuth();
    const { settings } = useSettings();
    const { news_unread, convocations_pending, communications_unread, messages_unread } = useNotifications();
    const navigate = useNavigate();
    const canAdmin = user && user.role === "admin";
    const canCoach = user && (user.role === "admin" || user.role === "coach");

    useEffect(() => {
        if (user) initPushNotifications(navigate);
    }, [user, navigate]);

    const tabs = [
        { to: "/", label: "Home", icon: HouseSimple, testid: "bottom-nav-home", end: true },
        { to: "/partite", label: "Partite", icon: CalendarBlank, testid: "bottom-nav-calendar", badge: convocations_pending },
        { to: "/messaggi", label: "Messaggi", icon: ChatCircleDots, testid: "bottom-nav-messages", badge: messages_unread },
        { to: "/news", label: "News", icon: Newspaper, testid: "bottom-nav-news", badge: news_unread },
        { to: "/profilo", label: "Profilo", icon: User, testid: "bottom-nav-profile" },
    ];
    const sideTabs = [
        { to: "/", label: "Home", icon: HouseSimple, testid: "side-home", end: true },
        { to: "/partite", label: "Partite", icon: CalendarBlank, testid: "side-calendar", badge: convocations_pending },
        { to: "/news", label: "News", icon: Newspaper, testid: "side-news", badge: news_unread },
        { to: "/comunicazioni", label: "Comunicazioni", icon: Megaphone, testid: "side-comms", badge: communications_unread },
        { to: "/messaggi", label: "Messaggi", icon: ChatCircleDots, testid: "side-messages", badge: messages_unread },
        { to: "/profilo", label: "Profilo", icon: User, testid: "side-profile" },
    ];

    return (
        <div className="min-h-dvh flex flex-col md:flex-row">
            {/* Sidebar (desktop) */}
            <aside className="hidden md:flex md:w-64 shrink-0 flex-col border-r border-border/60 bg-card/40 sticky top-0 h-dvh">
                <div className="px-5 py-6 border-b border-border/60">
                    <div className="flex items-center gap-3">
                        {settings?.logo_url ? (
                            <img
                                src={settings.logo_url}
                                alt="logo"
                                className="w-10 h-10 rounded-lg object-cover hairline"
                            />
                        ) : (
                            <div className="w-10 h-10 rounded-lg grid place-items-center bg-primary/15 text-primary hairline">
                                <Whistle size={22} weight="fill" />
                            </div>
                        )}
                        <div>
                            <div className="font-display text-lg font-bold leading-none tracking-tight">
                                {settings?.club_name || "Calcio Club"}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">
                                Stagione {settings?.current_season || "2025/2026"}
                            </div>
                        </div>
                    </div>
                </div>
                <nav className="flex-1 p-3 flex flex-col gap-1">
                    {sideTabs.map((t) => (
                        <SideItem key={t.to} {...t} />
                    ))}
                    {canCoach && (
                        <SideItem
                            to="/allenatore"
                            label="Area Allenatore"
                            icon={Whistle}
                            testid="side-coach"
                        />
                    )}
                    {canAdmin && (
                        <SideItem
                            to="/admin"
                            label="Amministrazione"
                            icon={GearSix}
                            testid="side-admin"
                        />
                    )}
                </nav>
                <div className="p-3 border-t border-border/60">
                    <div className="px-3 py-2 flex items-center justify-between">
                        <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{user?.name}</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                {roleLabels[user?.role]}
                            </div>
                        </div>
                        <button
                            data-testid="logout-btn-desktop"
                            onClick={() => {
                                logout();
                                navigate("/login");
                            }}
                            className="p-2 rounded-lg hover:bg-muted press"
                            aria-label="Esci"
                        >
                            <SignOut size={18} />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Mobile top bar */}
            <header className="md:hidden sticky top-0 z-30 glass border-b border-border/60 pt-safe">
                <div className="px-4 h-14 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {settings?.logo_url ? (
                            <img
                                src={settings.logo_url}
                                alt="logo"
                                className="w-8 h-8 rounded-md object-cover hairline"
                            />
                        ) : (
                            <div className="w-8 h-8 rounded-md grid place-items-center bg-primary/15 text-primary hairline">
                                <Whistle size={18} weight="fill" />
                            </div>
                        )}
                        <div className="font-display text-base font-bold uppercase tracking-tight">
                            {settings?.club_name || "Calcio Club"}
                        </div>
                    </div>
                    <button
                        data-testid="logout-btn-mobile"
                        onClick={() => {
                            logout();
                            navigate("/login");
                        }}
                        className="p-2 rounded-lg hover:bg-muted press"
                        aria-label="Esci"
                    >
                        <SignOut size={18} />
                    </button>
                </div>
            </header>

            {/* Main content */}
            <main className="flex-1 min-w-0 pb-24 md:pb-8">
                <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 md:py-8">
                    <Outlet />
                </div>
            </main>

            {/* Bottom nav (mobile) */}
            <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-border/60 pb-safe">
                <div className="flex items-stretch justify-around">
                    {tabs.slice(0, 5).map((t) => (
                        <BottomItem key={t.to} {...t} />
                    ))}
                </div>
            </nav>
        </div>
    );
}

function SideItem({ to, label, icon: Icon, testid, end, badge }) {
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg press text-sm font-medium ${
                    isActive
                        ? "bg-primary/15 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
            }
        >
            <span className="relative inline-flex">
                <Icon size={20} weight="duotone" />
                {badge > 0 && (
                    <span
                        data-testid={`${testid}-badge`}
                        className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center leading-none"
                    >
                        {badge > 9 ? "9+" : badge}
                    </span>
                )}
            </span>
            <span className="flex-1">{label}</span>
        </NavLink>
    );
}

function BottomItem({ to, label, icon: Icon, testid, end, badge }) {
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-1 py-2 press ${
                    isActive ? "text-primary" : "text-muted-foreground"
                }`
            }
        >
            <span className="relative inline-flex">
                <Icon size={22} weight={"duotone"} />
                {badge > 0 && (
                    <span
                        data-testid={`${testid}-badge`}
                        className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center leading-none"
                    >
                        {badge > 9 ? "9+" : badge}
                    </span>
                )}
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wider">{label}</span>
        </NavLink>
    );
}
